/**
 * RemoteOntologyLoader
 *
 * Service for loading ontologies from remote URLs.
 * Supports format detection and parsing of Turtle, RDF/XML, and OWL2/XML.
 * Automatically falls back to a local CORS proxy if direct fetch fails.
 */

import type { Ontology } from '@semantical/modules/owl2/types/Ontology.ts';
import { parseTurtle, parseOntology, parseRdfXml } from '@semantical/modules/owl2';

/**
 * Supported ontology formats.
 */
export type OntologyFormat = 'turtle' | 'rdfxml' | 'owl2xml';

/**
 * Result of loading an ontology from a remote URL.
 */
export interface LoadResult {
    /** Whether the load was successful */
    success: boolean;
    /** The parsed ontology (if successful) */
    ontology?: Ontology;
    /** Error message (if failed) */
    error?: string;
    /** The detected format */
    format?: OntologyFormat;
    /** The source URL */
    sourceUrl: string;
    /** Whether the proxy was used */
    usedProxy?: boolean;
}

/**
 * Progress callback for loading operations.
 */
export type ProgressCallback = (status: string) => void;

/** Default proxy server URL */
const DEFAULT_PROXY_URL = 'http://localhost:3001/proxy';

/**
 * Service for loading ontologies from remote URLs.
 *
 * Features:
 * - Auto-detects format from Content-Type, file extension, and content sniffing
 * - Supports Turtle, RDF/XML, and OWL2/XML formats
 * - Automatic fallback to CORS proxy when direct fetch fails
 * - Graceful error handling for network failures
 */
export class RemoteOntologyLoader {
    private proxyUrl: string;

    constructor(proxyUrl: string = DEFAULT_PROXY_URL) {
        this.proxyUrl = proxyUrl;
    }

    /**
     * Load an ontology from a remote URL.
     * Tries direct fetch first, falls back to proxy if CORS blocks the request.
     *
     * @param url - The URL to fetch the ontology from
     * @param onProgress - Optional callback for progress updates
     * @returns LoadResult indicating success/failure with the parsed ontology
     */
    async load(url: string, onProgress?: ProgressCallback): Promise<LoadResult> {
        // Extract a short name from the URL for display
        const shortName = this.extractShortName(url);

        onProgress?.(`Fetching ${shortName}...`);

        // First, try direct fetch
        const directResult = await this.fetchAndParse(url, false, onProgress);
        if (directResult.success) {
            return directResult;
        }

        // If direct fetch failed due to CORS, try proxy
        if (this.isCorsError(directResult.error)) {
            onProgress?.(`Using proxy for ${shortName}...`);
            console.debug(`RemoteOntologyLoader: Direct fetch failed (CORS), trying proxy...`);
            const proxyResult = await this.fetchAndParse(url, true, onProgress);
            if (proxyResult.success) {
                return { ...proxyResult, usedProxy: true };
            }

            // If proxy also failed, return a helpful message
            if (proxyResult.error?.includes('Failed to fetch') || proxyResult.error?.includes('NetworkError')) {
                return {
                    success: false,
                    error: `CORS blocked and proxy server unavailable. Start the proxy with: node server/proxy.js`,
                    sourceUrl: url
                };
            }
            return proxyResult;
        }

        return directResult;
    }

    /**
     * Extract a short display name from a URL.
     */
    private extractShortName(url: string): string {
        try {
            const urlObj = new URL(url);
            // Get the last path segment or the hostname
            const pathSegments = urlObj.pathname.split('/').filter(Boolean);
            if (pathSegments.length > 0) {
                const lastSegment = pathSegments[pathSegments.length - 1];
                // Remove common extensions
                return lastSegment.replace(/\.(ttl|rdf|owl|owx|xml)$/i, '');
            }
            return urlObj.hostname;
        } catch {
            return url.substring(0, 30) + (url.length > 30 ? '...' : '');
        }
    }

    /**
     * Check if an error is likely a CORS error.
     */
    private isCorsError(error?: string): boolean {
        if (!error) return false;
        return error.includes('Failed to fetch') ||
               error.includes('NetworkError') ||
               error.includes('CORS');
    }

    /**
     * Fetch and parse an ontology, optionally using the proxy.
     */
    private async fetchAndParse(url: string, useProxy: boolean, onProgress?: ProgressCallback): Promise<LoadResult> {
        try {
            const fetchUrl = useProxy
                ? `${this.proxyUrl}?url=${encodeURIComponent(url)}`
                : url;

            const response = await fetch(fetchUrl, {
                headers: {
                    'Accept': 'application/rdf+xml, text/turtle, application/owl+xml, application/xml, text/xml, */*'
                }
            });

            if (!response.ok) {
                // Try to get error details from proxy response
                if (useProxy && response.headers.get('Content-Type')?.includes('application/json')) {
                    const errorData = await response.json();
                    return {
                        success: false,
                        error: errorData.error || `HTTP ${response.status}: ${response.statusText}`,
                        sourceUrl: url
                    };
                }
                return {
                    success: false,
                    error: `HTTP ${response.status}: ${response.statusText}`,
                    sourceUrl: url
                };
            }

            const content = await response.text();
            const contentType = response.headers.get('Content-Type') || '';

            // Detect format
            const format = this.detectFormat(url, contentType, content);
            const shortName = this.extractShortName(url);
            onProgress?.(`Parsing ${shortName} (${format})...`);

            // Parse based on format
            const ontology = await this.parseContent(content, format);

            return {
                success: true,
                ontology,
                format,
                sourceUrl: url
            };
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error';
            return {
                success: false,
                error: message,
                sourceUrl: url
            };
        }
    }

    /**
     * Detect the ontology format from URL, Content-Type, and content.
     *
     * Priority: Content-Type > File extension > Content sniffing
     */
    private detectFormat(url: string, contentType: string, content: string): OntologyFormat {
        // 1. Check Content-Type header
        const ct = contentType.toLowerCase();
        if (ct.includes('text/turtle') || ct.includes('application/x-turtle') || ct.includes('text/n3')) {
            return 'turtle';
        }
        if (ct.includes('application/rdf+xml') || ct.includes('application/rdf')) {
            return 'rdfxml';
        }
        if (ct.includes('application/owl+xml')) {
            return 'owl2xml';
        }

        // 2. Check file extension
        const urlLower = url.toLowerCase();
        const ext = urlLower.split('.').pop()?.split('?')[0]; // Handle query strings
        if (ext === 'ttl' || ext === 'n3') {
            return 'turtle';
        }
        if (ext === 'rdf' || ext === 'owl') {
            return 'rdfxml';
        }
        if (ext === 'owx') {
            return 'owl2xml';
        }

        // 3. Content sniffing (first 500 chars)
        const sample = content.substring(0, 500).trim();

        // Check for Turtle syntax
        if (sample.startsWith('@prefix') || sample.startsWith('@base') ||
            sample.includes('@prefix ') || sample.includes('@base ')) {
            return 'turtle';
        }

        // Check for XML-based formats
        if (sample.startsWith('<?xml') || sample.startsWith('<rdf:RDF') ||
            sample.startsWith('<owl:Ontology') || sample.includes('<rdf:RDF')) {
            // Distinguish RDF/XML from OWL2/XML
            // OWL2/XML uses <Ontology> without rdf:RDF wrapper
            if (sample.includes('<Ontology') && !sample.includes('rdf:RDF')) {
                return 'owl2xml';
            }
            return 'rdfxml';
        }

        // Default to RDF/XML as it's the most common web format
        return 'rdfxml';
    }

    /**
     * Parse content using the appropriate parser based on format.
     */
    private async parseContent(content: string, format: OntologyFormat): Promise<Ontology> {
        switch (format) {
            case 'turtle':
                return await parseTurtle(content);
            case 'rdfxml':
                return await parseRdfXml(content);
            case 'owl2xml':
                return parseOntology(content);
            default:
                throw new Error(`Unsupported format: ${format}`);
        }
    }
}

/**
 * Singleton instance for convenience.
 */
export const remoteOntologyLoader = new RemoteOntologyLoader();
